import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { PagedQueryArg, QueryWhereValue } from 'src/apiClients/queries';
import { DataTable } from 'src/components/data-table/DataTable';
import type {
    DataTableCellValue,
    DataTableColDef,
    DataTableRow,
    DataTableSort,
    OnRowClick,
} from 'src/components/data-table/DataTable.types';
import { SortDirection, toCellString } from 'src/components/data-table/DataTable.types';
import { usePageFilter } from 'src/contexts/page-filter-context';

import { useStyles } from './EntityListPage.styles';
import { useEntityListQueryState } from './hooks/use-entity-list-query-state';

/**
 * Upper bound on pages auto-fetched while chasing a highlighted row, so a row
 * that can never appear doesn't page through the whole list indefinitely.
 */
const MAX_HIGHLIGHT_AUTO_FETCHES = 10;

/**
 * Shape an infinite paged query hook (RTK Query) returns when wired with
 * `DEFAULT_PAGE_SIZE` and the standard `{ rows, total }` envelope. Pages can
 * narrow `TRaw` to their endpoint's row type.
 */
export interface PagedInfiniteQueryResult<TRaw> {
    data?: { pages: Array<{ rows: TRaw[]; total: number }> };
    isLoading: boolean;
    isFetching: boolean;
    isError?: boolean;
    hasNextPage?: boolean;
    fetchNextPage: () => unknown;
}

export type PagedInfiniteQueryHook<TRaw> = (arg: PagedQueryArg) => PagedInfiniteQueryResult<TRaw>;

export interface EntityListPageProps<TRaw> {
    automationId: string;
    /** RTK Query infinite hook, e.g. `useGetUsersInfiniteQuery`. */
    useQuery: PagedInfiniteQueryHook<TRaw>;
    /** Server-side columns to OR-match against the user's debounced search input. */
    searchColumns: readonly string[];
    /**
     * UUID columns to match exactly (`$eq`) when the search term is a full,
     * valid UUID. These cannot use `$iLike` (Postgres type mismatch), so listing
     * them here keeps a `?q=<uuid>` deep link working instead of erroring.
     */
    uuidSearchColumns?: readonly string[];
    /** Initial sort applied on first render. */
    initialSort: DataTableSort[];
    /** Seeds the search box on first render (e.g. from a `?q=` deep link). */
    initialSearch?: string;
    columns: DataTableColDef[];
    /** Projects a raw row from the endpoint into a DataTable row. */
    mapRow: (raw: TRaw) => DataTableRow;
    onRowClick?: OnRowClick;
    /** Static filter conditions always applied to every query (e.g. subscriptionId). */
    baseWhere?: Record<string, QueryWhereValue>;
    /** Top-level query params appended outside of `where` (e.g. tenantId). */
    baseExtraParams?: Record<string, string>;
    titleWhenNoResults: string;
    messageWhenNoResults: string;
    /** When the query errors, show these instead of the no-results copy. */
    titleWhenLoadError?: string;
    messageWhenLoadError?: string;
    /** When provided, rows become expandable with a chevron toggle. */
    renderExpandedRow?: (row: DataTableRow) => ReactNode;
    /** Notified whenever the server-reported total changes. */
    onTotalChange?: (total: number) => void;
    /** When true, the table sizes to its content instead of filling available height. */
    autoHeight?: boolean;
    /**
     * When provided, called with the mapped rows so the page can enrich column
     * definitions with data-driven filter options. The returned columns replace
     * the static `columns` prop for the DataTable.
     */
    deriveColumns?: (baseColumns: DataTableColDef[], rows: DataTableRow[]) => DataTableColDef[];
    /** Highlights the matching row (by `row.id`) on first render. Cleared when the user interacts with the table. */
    highlightedRowId?: string | number;
    /** Filter values active on first render. Keys are column fields, values are selected option values. */
    initialFilters?: Record<string, DataTableCellValue[]>;
    /** Notified whenever the active filters change, e.g. to persist them across pages. */
    onFiltersChange?: (filters: Record<string, DataTableCellValue[]>) => void;
    /**
     * Display label used to group this table's filters in the "Filters on this page"
     * view when several tables register on the same page (e.g. an instance's
     * Users/Workspaces/Datasets sections). Defaults to `automationId`.
     */
    filterGroupLabel?: string;
}

/**
 * Page-level scaffold for any server-paged entity list: query state +
 * debounced search + DataTable wiring. Pages supply the endpoint hook,
 * columns, and per-row projection.
 */
export const EntityListPage = <TRaw,>({
    automationId,
    useQuery,
    searchColumns,
    uuidSearchColumns,
    initialSort,
    initialSearch,
    columns,
    mapRow,
    onRowClick,
    baseWhere,
    baseExtraParams,
    titleWhenNoResults,
    messageWhenNoResults,
    titleWhenLoadError,
    messageWhenLoadError,
    renderExpandedRow,
    onTotalChange,
    deriveColumns,
    autoHeight,
    highlightedRowId,
    initialFilters,
    onFiltersChange,
    filterGroupLabel,
}: EntityListPageProps<TRaw>) => {
    const { classes } = useStyles();

    const clientSortFields = useMemo(
        () => new Set(columns.filter((c) => c.clientSort).map((c) => c.field)),
        [columns],
    );

    const { queryState, setQueryState, queryArg } = useEntityListQueryState({
        searchColumns,
        initialSort,
        baseWhere,
        baseExtraParams,
        clientSortFields: clientSortFields.size > 0 ? clientSortFields : undefined,
        initialSearch,
        uuidSearchColumns,
        initialFilters,
    });

    const { data, isLoading, isFetching, isError, fetchNextPage, hasNextPage } = useQuery(queryArg);

    const mappedRows = useMemo<DataTableRow[]>(() => {
        if (!data) return [];
        // Defensive de-duplication: adjacent offset/limit pages can briefly
        // overlap (e.g. when the server order isn't yet total, or rows shift
        // between fetches), which would surface duplicate React keys since rows
        // are keyed by `id`. Keep the first occurrence of each id.
        const seen = new Set<DataTableRow['id']>();
        const deduped: DataTableRow[] = [];
        for (const raw of data.pages.flatMap((p) => p.rows)) {
            const row = mapRow(raw);
            if (seen.has(row.id)) continue;
            seen.add(row.id);
            deduped.push(row);
        }
        return deduped;
    }, [data, mapRow]);

    // Apply client-side sorting for columns marked with clientSort. These
    // fields were excluded from the server query so they must be sorted here
    // on the already-loaded rows.
    const rows = useMemo<DataTableRow[]>(() => {
        const clientSorts = queryState.sorts.filter((s) => clientSortFields.has(s.field));
        if (clientSorts.length === 0) return mappedRows;
        return [...mappedRows].sort((a, b) => {
            for (const { field, direction } of clientSorts) {
                const cmp = toCellString(a[field]).localeCompare(
                    toCellString(b[field]),
                    undefined,
                    {
                        numeric: true,
                        sensitivity: 'base',
                    },
                );
                if (cmp !== 0) return direction === SortDirection.ASC ? cmp : -cmp;
            }
            return 0;
        });
    }, [mappedRows, queryState.sorts, clientSortFields]);

    const total = data?.pages[0]?.total ?? 0;
    const isLoadingMore = isFetching && !isLoading && rows.length > 0;
    const errored = !!isError;

    // Cache derived columns from the first unfiltered data load so that
    // filter options don't shrink when filters narrow the server response.
    // Invalidate when the base `columns` reference changes so a parent that
    // swaps its column set (locale, feature flag, responsive) is re-derived.
    const derivedColumnsRef = useRef<DataTableColDef[] | null>(null);
    const lastColumnsRef = useRef<DataTableColDef[] | null>(null);

    const resolvedColumns = useMemo(() => {
        if (!deriveColumns) return columns;
        if (lastColumnsRef.current !== columns) {
            lastColumnsRef.current = columns;
            derivedColumnsRef.current = null;
        }
        if (rows.length > 0 && !derivedColumnsRef.current) {
            derivedColumnsRef.current = deriveColumns(columns, rows);
        }
        return derivedColumnsRef.current ?? columns;
    }, [deriveColumns, columns, rows]);

    useEffect(() => {
        if (data) onTotalChange?.(total);
    }, [data, total, onTotalChange]);

    const { register, unregister } = usePageFilter();

    const handlePageFilterChange = useCallback(
        (field: string, values: DataTableCellValue[]) => {
            setQueryState((prev) => ({ ...prev, filters: { ...prev.filters, [field]: values } }));
        },
        [setQueryState],
    );

    // Build from `resolvedColumns` (post-`deriveColumns`) so the filter registry
    // reflects the columns the user actually sees, including any data-driven
    // filter options added at render time.
    const filterableColumns = useMemo(
        () =>
            resolvedColumns
                .filter((c) => c.filterOptions && c.filterOptions.length > 0)
                .map((c) => ({
                    field: c.field,
                    headerName: c.headerName,
                    filterOptions: c.filterOptions!,
                })),
        [resolvedColumns],
    );

    useEffect(() => {
        if (filterableColumns.length === 0) return;
        register({
            id: automationId,
            label: filterGroupLabel ?? automationId,
            columns: filterableColumns,
            filters: queryState.filters,
            onFilterChange: handlePageFilterChange,
        });
    }, [
        register,
        automationId,
        filterGroupLabel,
        filterableColumns,
        queryState.filters,
        handlePageFilterChange,
    ]);

    useEffect(() => {
        return () => unregister(automationId);
    }, [unregister, automationId]);

    useEffect(() => {
        onFiltersChange?.(queryState.filters);
    }, [onFiltersChange, queryState.filters]);

    // When a specific row is highlighted but hasn't loaded yet, keep fetching
    // pages until it arrives. This lets the typeahead highlight any row in the
    // full unfiltered list without server-side pre-filtering. The fetch count is
    // bounded so a row that can never appear (e.g. filtered out, deleted) can't
    // page through the entire list forever.
    const highlightFetchCountRef = useRef(0);
    useEffect(() => {
        highlightFetchCountRef.current = 0;
    }, [highlightedRowId]);
    useEffect(() => {
        if (!highlightedRowId || !data || isFetching || !hasNextPage) return;
        if (highlightFetchCountRef.current >= MAX_HIGHLIGHT_AUTO_FETCHES) return;
        const found = rows.some((row) => row.id === highlightedRowId);
        if (!found) {
            highlightFetchCountRef.current += 1;
            fetchNextPage();
        }
    }, [highlightedRowId, rows, isFetching, hasNextPage, fetchNextPage]);

    return (
        <Box className={classes.root} automation-id={automationId}>
            <Box className={classes.content}>
                <DataTable
                    columns={resolvedColumns}
                    rows={errored ? [] : rows}
                    isLoading={isLoading && !errored}
                    onRowClick={onRowClick}
                    queryState={queryState}
                    onQueryStateChange={setQueryState}
                    totalRows={total}
                    hasMore={!!hasNextPage}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={fetchNextPage}
                    titleWhenNoResults={
                        errored && titleWhenLoadError ? titleWhenLoadError : titleWhenNoResults
                    }
                    messageWhenNoResults={
                        errored && messageWhenLoadError
                            ? messageWhenLoadError
                            : messageWhenNoResults
                    }
                    renderExpandedRow={renderExpandedRow}
                    autoHeight={autoHeight}
                    highlightedRowId={highlightedRowId}
                />
            </Box>
        </Box>
    );
};
